import {
  Badge,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  Icons,
} from '@commenda/kakashi';
import {
  IN_APP_REFERRAL_CREDIT_CENTS,
  IN_APP_REFERRAL_TIERS,
} from '@commenda/types';
import Image from 'next/image';
import { useRouter } from 'next/router';

import { InAppReferralHelper } from 'components/settings/referrals/in-app-referral-helper';

const TOP_TIER = IN_APP_REFERRAL_TIERS[IN_APP_REFERRAL_TIERS.length - 1];

/** Each card is capped at the tier held when approved, so this is NOT top cap x top threshold. */
const MAX_ANNUAL_PAYOUT_CENTS = IN_APP_REFERRAL_TIERS.reduce(
  (total, tier, index) => {
    const next = IN_APP_REFERRAL_TIERS[index + 1];
    if (!next) return total;
    return (
      total +
      (next.minApprovedReferrals - tier.minApprovedReferrals) *
        tier.giftCardCapCents
    );
  },
  0
);

const REFERRAL_HIGHLIGHTS = [
  {
    icon: Icons.Gift01,
    text: `Every referral that buys earns you a gift card, worth up to ${InAppReferralHelper.formatCents(TOP_TIER?.giftCardCapCents ?? 0)}.`,
  },
  {
    icon: Icons.Award04,
    text: `Climb Bronze to Platinum. ${TOP_TIER?.minApprovedReferrals ?? 50} approved referrals is up to ${InAppReferralHelper.formatCents(MAX_ANNUAL_PAYOUT_CENTS)} a year.`,
  },
  {
    icon: Icons.Globe01,
    text: `They start with ${InAppReferralHelper.formatCents(IN_APP_REFERRAL_CREDIT_CENTS)} in credit to open or expand in 70+ countries.`,
  },
];

export function ReferralAnnouncementModal({
  onClose,
}: {
  onClose: () => void;
}) {
  const router = useRouter();

  const onOpenProgram = () => {
    onClose();
    router.push('/settings/referrals');
  };

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent
        hideCloseIcon
        aria-describedby={undefined}
        className="grid w-[740px] !max-w-[95vw] grid-cols-1 gap-0 overflow-hidden rounded-xl p-0 sm:grid-cols-[minmax(0,1fr)_minmax(0,1fr)]"
      >
        <div className="flex flex-col gap-3xl p-5xl">
          <div className="flex flex-col gap-xl">
            <Badge variant="blue" size="sm" className="self-start">
              New
            </Badge>
            <DialogTitle className="display-xs-bold text-text-primary">
              Earn up to{' '}
              {InAppReferralHelper.formatCents(MAX_ANNUAL_PAYOUT_CENTS)} a year
              from referrals
            </DialogTitle>
            <ul className="flex flex-col gap-lg">
              {REFERRAL_HIGHLIGHTS.map(({ icon: Icon, text }) => (
                <li key={text} className="flex items-start gap-lg">
                  <Icon
                    aria-hidden
                    className="mt-0.5 size-5 shrink-0 text-fg-brand"
                  />
                  <p className="txt-small-regular text-text-secondary">
                    {text}
                  </p>
                </li>
              ))}
            </ul>
          </div>

          <Button
            size="lg"
            fullWidth
            className="mt-auto"
            onClick={onOpenProgram}
          >
            Get your referral link
          </Button>
        </div>

        <div className="relative hidden min-h-[360px] bg-bg-brand-solid sm:block">
          <Image
            src="/referrals/referral-banner-building.png"
            alt=""
            width={2356}
            height={1326}
            className="pointer-events-none absolute inset-0 h-full w-full object-cover object-right-bottom"
            priority
          />
          <button
            type="button"
            onClick={onClose}
            aria-label="Dismiss"
            className="absolute right-xl top-xl flex size-8 items-center justify-center rounded-full bg-white/90 text-fg-primary transition-colors hover:bg-white"
          >
            <Icons.XClose className="size-4" />
          </button>
        </div>

        <button
          type="button"
          onClick={onClose}
          aria-label="Dismiss"
          className="absolute right-lg top-lg flex size-8 items-center justify-center rounded-full bg-white/90 text-fg-primary transition-colors hover:bg-white sm:hidden"
        >
          <Icons.XClose className="size-4" />
        </button>
      </DialogContent>
    </Dialog>
  );
}
